import { type ComponentProps, useCallback, useLayoutEffect } from 'react';
import type { Modal } from 'react-native';

import { useSelector } from '@xstate5/react';
import { type SnapshotFrom } from 'xstate5';

import { type modalMachine, useModalMachine } from '../../modal-machine';

/**
 * Wrapper around the Modal Machine integration.
 */
export function useModalMachineIntegration(props: ModalMachineIntegrationProps) {
  const { visible, animationType, onShowing, onShown, onDismissing, onDismissed } = props;

  const modalMachineInitialState = visible ? 'shown' : 'dismissed';
  const { modalService, helpers: modalServiceHelpers } = useModalMachine({
    initial: modalMachineInitialState,
    onShowing,
    onShown,
    onDismissing,
    onDismissed,
  });

  const isShowing = useSelector(modalService, checkIfShowing);
  const isShown = useSelector(modalService, checkIfShown);
  const isDismissing = useSelector(modalService, checkIfDismissing);
  const isDismissed = useSelector(modalService, checkIfDismissed);

  const syncAnimationState = useCallback(() => {
    if (animationType === 'none') return;

    if (visible) {
      modalServiceHelpers.sendStartShowEvent();

      return;
    }

    modalServiceHelpers.sendStartDismissEvent();
  }, [animationType, modalServiceHelpers, visible]);

  useLayoutEffect(syncAnimationState, [syncAnimationState]);

  return {
    modalState: {
      isShowing,
      isShown,
      isDismissing,
      isDismissed,
    },
    modalServiceHelpers,
  };
}

// ─── Selectors ─────────────────────────────────────────────────────────

function checkIfShowing(snapshot: ModalMachineSnapshot) {
  return snapshot.matches({ animation: 'showing' });
}

function checkIfShown(snapshot: ModalMachineSnapshot) {
  return snapshot.matches({ visibility: 'shown' });
}

function checkIfDismissing(snapshot: ModalMachineSnapshot) {
  return snapshot.matches({ animation: 'dismissing' });
}

function checkIfDismissed(snapshot: ModalMachineSnapshot) {
  return snapshot.matches({ visibility: 'dismissed' });
}

// ─── Types ─────────────────────────────────────────────────────────────

type ModalMachineSnapshot = SnapshotFrom<typeof modalMachine>;

type ModalMachineIntegrationProps = Readonly<{
  visible?: boolean;
  animationType: ComponentProps<typeof Modal>['animationType'];
  onShowing?: () => void;
  onShown?: () => void;
  onDismissing?: () => void;
  onDismissed?: () => void;
}>;
